//Dependencies............................................
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Alert, Button, Flex, Form as FormAntd, Input, Modal, Space, Spin, Tabs, Tag, Typography } from "antd";
import { CodeOutlined, ControlOutlined, DownloadOutlined, FormOutlined, PartitionOutlined, SyncOutlined } from "@ant-design/icons";
import Form from "@rjsf/antd";
import { RegistryFieldsType, RJSFSchema } from "@rjsf/utils";
import validator from "@rjsf/validator-ajv8";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

//Components..............................................
import { BASE_URL } from "../../../utils/constants";
import {
  JsonSchemaItem,
  VehicleItem,
  VehicleJsonConfig,
} from "../../../utils/interfaces";

/* CONF 1.0 */
import Conf10_uiSchema from "../../../Components/VehicleConfigPageComp/Modals/CONF_1_0_Modal/schema-json/car-config.uischema.json";
import CONF_1_0_Modal from "../../../Components/VehicleConfigPageComp/Modals/CONF_1_0_Modal/CONF_1_0_Modal";

/* CONF 2.0 */
import Conf20_uiSchema from "../../../Components/VehicleConfigPageComp/Modals/CONF_2_0_Modal/schema-json/car-config.uischema.json";
import CONF_2_0_Modal from "../../../Components/VehicleConfigPageComp/Modals/CONF_2_0_Modal/CONF_2_0_Modal";

/* CONF 3.0 */
import Conf30_uiSchema from "../../../Components/VehicleConfigPageComp/Modals/CONF_3_0_Modal/schema-json/car-config.uischema.json";
import CONF_3_0_Modal, { Conf30ActiveTabContext } from "../../../Components/VehicleConfigPageComp/Modals/CONF_3_0_Modal/CONF_3_0_Modal";
import { NotificationInstance } from "antd/es/notification/interface";
import { useHasAction } from "../../../hooks/useHasAction";

const { Text } = Typography;

interface Props {
  vehicle_name: string;
  schema_id: number;
  config_id: number;
  showModal: boolean;
  vehicle: VehicleItem;
  onClose: (loading:boolean) => void;
  api: NotificationInstance
}

type VehicleJsonConfigResponse = {
    success: boolean,
    content: VehicleJsonConfig
}

type JsonSchemaResponse = {
    success: boolean,
    content: JsonSchemaItem
}


const EditVehicleConfigModal: React.FC<Props> = ({ vehicle_name, schema_id, config_id, showModal, onClose, vehicle, api }: Props) => {

    const canDownloadConfigBinary: boolean = useHasAction("vehicle_config_download_binary");
    const canEditConfig: boolean = useHasAction("vehicle_config_edit_config");

    const [form] = FormAntd.useForm();
    const queryClient = useQueryClient()

    const [jsonValid, setJsonValid] = useState<boolean>(true)

    const [loading, setLoading] = useState<boolean>(false);

    // CONF 3.0 renders its CAN+LIN config, functionality control and raw JSON
    // as 3 top-level tabs rather than nesting them under a single RJSF tab.
    const [conf3ActiveTab, setConf3ActiveTab] = useState<string>('control');

    const vehicleJsonConfigQuery = useQuery({
        queryKey: ['vehicleJsonConfig', config_id, schema_id],
        queryFn: async () => {
            const res = await axios.get<VehicleJsonConfigResponse>(`${BASE_URL}/get_json_config/${config_id}`)
            // setJsonConfigData(JSON.stringify(res.data.content.json_config, undefined, 4))
            return res.data.content
        },
        enabled: schema_id !== 0 && config_id !== 0,
        // This modal feeds the RJSF Form's `formData` directly from this
        // query's cache, and remounts the Form (via a key tied to
        // dataUpdatedAt) whenever a fetch completes - a background refetch
        // triggered by switching browser tabs back into focus (react-query's
        // default) would silently overwrite in-progress unsaved edits and
        // reset every Collapse section back to closed. Only ever refetch
        // here from an explicit action (opening the modal, saving).
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    })

    const jsonSchemaQuery = useQuery({
        queryKey: ['jsonSchemaConfig', config_id, schema_id],
        queryFn: async () => {
            const res = await axios.get<JsonSchemaResponse>(`${BASE_URL}/get_json_schemas_base/${schema_id}`)
            return res.data.content
        },
        enabled: schema_id !== 0 && config_id !== 0,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    })

    useEffect(() => {
        if (vehicleJsonConfigQuery.data && showModal){
            let data = JSON.stringify(vehicleJsonConfigQuery.data.json_config, undefined, 4)
            // setJsonConfigData(()=>data);
            form.setFieldValue("json_config", data);
            // console.log("Config data variable: ", data)
        }
    //     eslint-disable-next-line
    }, [vehicleJsonConfigQuery.data, showModal ])

    const downloadConfigBinary = (vehicleName: string, configId: number) => {
        axios({
            url: `${BASE_URL}/get_vehicle_binary/${configId}`, //your url
            method: 'GET',
            responseType: 'blob', // important
        }).then((response) => {
            // create file link in browser's memory
            const href = URL.createObjectURL(response.data);

            // create "a" HTML element with href to file & click
            const link = document.createElement('a');
            link.href = href;
            const filename = "CANNY_CONFIG_" + vehicleName + ".bin"

            link.setAttribute('download', filename); //or any other extension
            document.body.appendChild(link);
            link.click();

            // clean up "a" element & remove ObjectURL
            document.body.removeChild(link);
            URL.revokeObjectURL(href);
            api.success({
                title: 'Success',
                description: 'Vehicle binary downloaded successfully',
                placement: 'bottomRight',
                showProgress: true
            });
        }).catch((error) => {
            api.error({
                title: 'Error',
                description: error.message,
                placement: 'bottomRight',
                showProgress: true
            });
        });
    }

    const updateVehicleMutation = useMutation({
        mutationFn: (values: VehicleItem) => {
            return axios.post(`${BASE_URL}/update_json_config/${config_id}`, values, {
                headers: {
                // Overwrite Axios's automatically set Content-Type
                'Content-Type': 'application/json'
              }})
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['vehicleList'] });
            queryClient.invalidateQueries({ queryKey: ['vehicleJsonConfig', config_id, schema_id] });
        },

        onSettled: () => setLoading(false),
    })

    const onFinish = (values: VehicleItem) => {
        setLoading(true)
        console.log('Received values of form: ', JSON.parse(values.json_config as string));
        values.json_config = JSON.parse(values.json_config as string)
        // var formData = new FormData;
        // formData.append('json_config', JSON.parse(values.json_config))
        updateVehicleMutation.mutate(values, {
        onSuccess: () =>  {
            onClose(loading);
            api.success({
                title: 'Success',
                description: 'Vehicle configuration updated successfully',
                placement: 'bottomRight',
                showProgress: true
            });
        },
        onError: (error) => {
            api.error({
                title: 'Error updating vehicle',
                description: error.message,
                placement: 'bottomRight',
                showProgress: true
            });
        }
        });
    };

    const configType = `${vehicleJsonConfigQuery.data && vehicleJsonConfigQuery.data.status}`
    const jsonSchemaName = `${jsonSchemaQuery.data && jsonSchemaQuery.data.version_schema_name}`
    const isConf30 = jsonSchemaName === 'CONF 3.0'

    const vehicleId = vehicleJsonConfigQuery.data?.vehicle_id
    const vehicleStatus = vehicleJsonConfigQuery.data?.status

    // Each CONF version migrates from the one before it.
    const MIGRATION_SOURCE: Record<string, string> = { "CONF 2.0": "CONF 1.0", "CONF 3.0": "CONF 2.0" }
    const confFrom = MIGRATION_SOURCE[jsonSchemaName]
    const confTo = jsonSchemaName

    const validTest = confFrom ? vehicle.json_config_versions?.[confFrom]?.TEST?.valid : false
    const validRelease = confFrom ? vehicle.json_config_versions?.[confFrom]?.RELEASE?.valid : false

    const getModalTitle = () => {
        return (
            <Flex justify="space-between" align="center" style={{ paddingRight: 32 }}>
                <Space orientation="vertical" size={2}>
                    <Text strong style={{ fontSize: 16 }}>{vehicle_name}</Text>
                    <Space size={6}>
                        <Tag color={configType === 'RELEASE' ? 'green' : 'gold'}>{configType}</Tag>
                        <Tag color="blue">{jsonSchemaName}</Tag>
                        {!canEditConfig && <Tag>View Only</Tag>}
                    </Space>
                </Space>
                <Space>
                    <Button
                        icon={<DownloadOutlined />}
                        onClick={() => downloadConfigBinary(vehicle_name, config_id)}
                        disabled={loading || !canDownloadConfigBinary}
                    >
                        Download Binary
                    </Button>
                    {(confFrom && vehicleStatus === 'TEST') && (
                        <Button icon={<SyncOutlined />} disabled={!validTest || loading || !canEditConfig} onClick={() => updateFromConf1()}>
                            Update from {confFrom}
                        </Button>
                    )}
                    {(confFrom && vehicleStatus === 'RELEASE') && (
                        <Button icon={<SyncOutlined />} disabled={!validRelease || loading || !canEditConfig} onClick={() => updateFromConf1()}>
                            Update from {confFrom}
                        </Button>
                    )}
                </Space>
            </Flex>
        )
    }

    const updateFromConf1 = () => {
        axios
        .get<VehicleJsonConfigResponse>(`${BASE_URL}/migrate_conf/${confFrom}/${confTo}/${vehicleStatus}/${vehicleId}`)
        .then((response) => {
            let data = JSON.stringify(response.data.content, undefined, 4)
            form.setFieldValue("json_config", data);
            queryClient.setQueryData(
                ['vehicleJsonConfig', config_id, schema_id],
                (old: VehicleJsonConfig | undefined) => old ? { ...old, json_config: response.data.content } : old
            );
            console.log('response', data)
            api.success({
                title: 'Success',
                description: `Successfully updated from ${confFrom}`,
                placement: 'bottomRight',
                showProgress: true
            });
        })
        .catch((error) => {
          console.error(`Error updating from ${confFrom}:`, error);
          api.error({
              title: 'Error',
              description: (error as any).message,
              placement: 'bottomRight',
              showProgress: true
          });
        });
    }

    const selectJsonSchemaID = () => {
        if(jsonSchemaName === 'CONF 1.0') {
            return Conf10_uiSchema
        }
        else if(jsonSchemaName === 'CONF 2.0') {
            return Conf20_uiSchema
        }
        else if(jsonSchemaName === 'CONF 3.0') {
            return Conf30_uiSchema
        }else{
            return undefined
        }
    }

    const Fields: RegistryFieldsType = {
        conf_1_0_modal: CONF_1_0_Modal,
        conf_2_0_modal: CONF_2_0_Modal,
        conf_3_0_modal: CONF_3_0_Modal,
    };

    const dataReady = !!vehicleJsonConfigQuery.data && !!jsonSchemaQuery.data;

    const initialJsonConfigString = vehicleJsonConfigQuery.data
        ? JSON.stringify(vehicleJsonConfigQuery.data.json_config, undefined, 4)
        : undefined;
    const watchedJsonConfig = FormAntd.useWatch("json_config", form);
    const isDirty = watchedJsonConfig !== undefined
        && initialJsonConfigString !== undefined
        && watchedJsonConfig !== initialJsonConfigString;


    return(
        <Modal
            forceRender={true}
            title={getModalTitle()}
            loading={vehicleJsonConfigQuery.isLoading || jsonSchemaQuery.isLoading || loading}
            open={showModal}
            okText={!canEditConfig ? "View Only" : (jsonValid ? "Update" : "JSON invalid")}
            onOk={() =>{
                form.submit();
            }}
            cancelText="Cancel"
            width={1000}
            okButtonProps={{
                autoFocus: false,
                htmlType: 'submit',
                loading: loading,
                disabled: !jsonValid || !canEditConfig || !isDirty || loading,
                danger: !jsonValid
            }}
            cancelButtonProps={{ disabled: loading }}
            closable={!loading}
            onCancel={() => onClose(loading)}
            maskClosable={!loading}
        >
            {!canEditConfig && (
                <Alert
                    type="warning"
                    showIcon
                    title="You do not have permission to edit this configuration. Viewing only."
                    style={{ marginBottom: 16 }}
                />
            )}
            {(() => {
                if (!dataReady) {
                    return (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0' }}>
                            <Spin size="large" />
                        </div>
                    );
                }
                const rjsfFormElement = (
                    <Conf30ActiveTabContext.Provider value={conf3ActiveTab}>
                        <Form
                            key={`${config_id}-${vehicleJsonConfigQuery.dataUpdatedAt}-${jsonSchemaQuery.dataUpdatedAt}`}
                            schema={jsonSchemaQuery.data?.json_schema ? jsonSchemaQuery.data?.json_schema : {} as RJSFSchema}
                            validator={validator}
                            fields={Fields}
                            formData={vehicleJsonConfigQuery.data?.json_config ? vehicleJsonConfigQuery.data?.json_config : {}}
                            liveValidate
                            disabled={!canEditConfig}
                            onChange={(e) => {
                                form.setFieldValue("json_config", JSON.stringify(e.formData, undefined, 4))
                                form.validateFields()
                            }}
                            uiSchema={selectJsonSchemaID()}
                            formContext={vehicleJsonConfigQuery.data?.json_config ? vehicleJsonConfigQuery.data?.json_config : {}}
                            />
                    </Conf30ActiveTabContext.Provider>
                )
                const rawJsonFormElement = (
                    <FormAntd
                        form={form}
                        name="form_edit_json_config"
                        clearOnDestroy
                        disabled={!canEditConfig}
                        onFinish={(values:VehicleItem) => onFinish(values)}
                    >
                        <FormAntd.Item
                            name="json_config"
                            initialValue={form.getFieldValue("json_config")}

                            rules={[
                                {
                                required: true,
                                validator: (_, value: string) =>{
                                        if(value) {
                                            try {
                                                JSON.parse(value);
                                                setJsonValid(true)
                                                return Promise.resolve()
                                            } catch (e) {
                                                setJsonValid(false)
                                                return Promise.reject(new Error('Invalid JSON'))
                                            }
                                        } else {
                                            setJsonValid(false)
                                            return Promise.reject(new Error('Please enter JSON Schema'))
                                        }
                                    }
                                },
                            ]}>
                            <Input.TextArea
                                rows={30}
                                disabled={!canEditConfig}
                                style={{ fontFamily: "Consolas, Monaco, 'Courier New', monospace", fontSize: 13 }}
                                onChange={(e:any) => {
                                    form.setFieldValue("json_config",e.target.value)
                                }}/>
                        </FormAntd.Item>
                    </FormAntd>
                )

                if (isConf30) {
                    return (
                        <>
                            <Tabs
                                activeKey={conf3ActiveTab}
                                onChange={setConf3ActiveTab}
                                items={[
                                    { key: 'control', label: <span><ControlOutlined /> JSON Config Control</span>, children: null },
                                    { key: 'functionality', label: <span><PartitionOutlined /> Functionality</span>, children: null },
                                    { key: 'json', label: <span><CodeOutlined /> JSON Config</span>, children: null },
                                ]}
                            />
                            {/* Both RJSF-driven tabs are the same single <Form> instance (kept
                                mounted and shown/hidden via CSS) - two separate Form mounts would
                                each track their own internal state and silently drop whichever
                                tab's edits weren't visible when the other's onChange last fired. */}
                            <div style={{ display: conf3ActiveTab === 'json' ? 'none' : 'block' }}>
                                {rjsfFormElement}
                            </div>
                            <div style={{ display: conf3ActiveTab === 'json' ? 'block' : 'none' }}>
                                {rawJsonFormElement}
                            </div>
                        </>
                    )
                }

                return (
                    <Tabs>
                        <Tabs.TabPane tab={<span><FormOutlined /> JSON Config Control</span>} key='general' forceRender>
                            {rjsfFormElement}
                        </Tabs.TabPane>
                        <Tabs.TabPane tab={<span><CodeOutlined /> JSON Config</span>} key='2' forceRender>
                            {rawJsonFormElement}
                        </Tabs.TabPane>
                    </Tabs>
                )
            })()}
        </Modal>
    )
}
export default EditVehicleConfigModal;

